import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Upload, Download, Search, Flame, TrendingUp, Snowflake,
  Trash2, Sparkles, ArrowRight, ArrowLeftRight, X, Users, Coffee, Salad, Dumbbell, Mountain,
  MessageCircle, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_URL, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PhoneInput from "@/components/PhoneInput";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// B2C destinations for the Growth Engine promote dropdown. Unlike PipelineHome's
// bulk-MOVE this list stays complete for Growth — every B2C pipeline is a valid
// promote target because we CLONE the lead, we don't move it.
const PROMOTE_TARGETS = [
  { key: "b2c_intel",    label: "B2C Intelligence (staging)", icon: Users,    accent: "text-primary" },
  { key: "b2c_coffee",   label: "B2C Coffee",                 icon: Coffee,   accent: "text-amber-600" },
  { key: "b2c_jaydiets", label: "B2C JAYDIETs",               icon: Salad,    accent: "text-emerald-600" },
  { key: "b2c_wellness", label: "B2C Wellness",               icon: Dumbbell, accent: "text-fuchsia-600" },
  { key: "b2c_retreat",  label: "B2C Retreat",                icon: Mountain, accent: "text-sky-600" },
];

const STATUS_COLORS = {
  new: "bg-secondary text-foreground",
  contacted: "bg-blue-500/15 text-blue-500",
  qualified: "bg-primary/15 text-primary",
  meeting: "bg-purple-500/15 text-purple-500",
  won: "bg-emerald-500/15 text-emerald-500",
  lost: "bg-destructive/15 text-destructive",
  disqualified: "bg-red-500/15 text-red-500",
  deferred: "bg-amber-500/15 text-amber-500",
};
const STATUSES = ["new", "contacted", "qualified", "meeting", "won", "lost", "disqualified", "deferred"];

// Compact B2B / B2C badge inferred from the lead's pipeline field.
// `b2b_growth` = B2B, every `b2c_*` = B2C. Nulls fall back to B2B for
// backwards-compat with legacy leads that pre-date the pipeline field.
function AudienceBadge({ pipeline }) {
  const isB2C = typeof pipeline === "string" && pipeline.startsWith("b2c_");
  const label = isB2C ? "B2C" : "B2B";
  const cls = isB2C
    ? "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30"
    : "bg-sky-500/15 text-sky-600 dark:text-sky-300 border-sky-500/30";
  return (
    <span
      title={pipeline ? `Pipeline: ${pipeline}` : "Pipeline: (legacy)"}
      className={"text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border " + cls}
      data-testid={`lead-audience-${label.toLowerCase()}`}
    >
      {label}
    </span>
  );
}

function TempBadge({ t }) {
  if (!t) return <span className="text-xs text-muted-foreground">—</span>;
  const map = { hot: [Flame, "text-orange-500"], warm: [TrendingUp, "text-amber-500"], cold: [Snowflake, "text-sky-500"] };
  const [Icon, cls] = map[t] || [];
  return (
    <span className="inline-flex items-center gap-1 text-xs capitalize">
      {Icon && <Icon className={"h-3.5 w-3.5 " + cls} />} {t}
    </span>
  );
}

/* ---- New Lead Modal ---- */
function NewLeadDialog({ open, onClose, onCreated, meta }) {
  const [form, setForm] = useState({
    business_name: "", industry: "", website: "", email: "", phone: "",
    city: "", country: "", decision_maker_name: "", decision_maker_role: "",
    status: "new", notes: "",
  });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name) { toast.error("Business name is required."); return; }
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      const { data } = await api.post("/growth/leads", { ...clean, business_name: form.business_name });
      toast.success("Lead added.");
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create lead.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-background border border-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl">Add lead</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-foreground/10" data-testid="lead-dialog-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Business name *</label>
            <input required data-testid="lead-name" value={form.business_name} onChange={upd("business_name")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Industry</label>
            <select data-testid="lead-industry" value={form.industry} onChange={upd("industry")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
              <option value="">— Select —</option>
              {(meta?.industries || []).map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Status</label>
            <select data-testid="lead-status" value={form.status} onChange={upd("status")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm capitalize">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Website</label>
            <input data-testid="lead-website" value={form.website} onChange={upd("website")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" placeholder="https://…" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Email</label>
            <input type="email" data-testid="lead-email" value={form.email} onChange={upd("email")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Phone</label>
            <div className="mt-1.5">
              <PhoneInput
                value={form.phone}
                defaultCountry={form.country}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                testId="lead-phone"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">City</label>
            <input value={form.city} onChange={upd("city")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Country</label>
            <input value={form.country} onChange={upd("country")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Decision maker name</label>
            <input value={form.decision_maker_name} onChange={upd("decision_maker_name")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Role</label>
            <input value={form.decision_maker_role} onChange={upd("decision_maker_role")} className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm" placeholder="Owner, Head Barista…" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Notes</label>
            <textarea value={form.notes} onChange={upd("notes")} rows={3} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="lead-save">{busy ? "Saving…" : "Save lead"}</Button>
        </div>
      </form>
    </div>
  );
}

export default function GrowthLeads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tempFilter, setTempFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [showNew, setShowNew] = useState(false);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState({});  // { leadId: true }
  const fileRef = useRef(null);
  const PER = 25;

  const selectedIds = useMemo(() => Object.keys(selection).filter((k) => selection[k]), [selection]);
  const [waSending, setWaSending] = useState(false);
  const [waTemplates, setWaTemplates] = useState([]);
  const [preflight, setPreflight] = useState(null);   // { ready_count, skipped_count, ready[], skipped[] }
  const [preflightLoading, setPreflightLoading] = useState(false);
  useEffect(() => {
    api.get("/whatsapp/templates").then(({ data }) => setWaTemplates(data.items || []))
      .catch(() => {});
  }, []);

  // Preflight recomputes 250ms after any change to selection, so operators
  // see "3 of 5 ready · 2 will be skipped" before firing the API.
  useEffect(() => {
    if (!selectedIds.length) { setPreflight(null); return; }
    setPreflightLoading(true);
    const t = setTimeout(() => {
      api.post("/whatsapp/preflight", { lead_ids: selectedIds })
        .then(({ data }) => setPreflight(data))
        .catch(() => setPreflight(null))
        .finally(() => setPreflightLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [selectedIds]);
  const sendWhatsApp = async (ids, templateId = null) => {
    const targets = ids?.length ? ids : selectedIds;
    if (!targets.length) return;
    setWaSending(true);
    try {
      const payload = { lead_ids: targets };
      if (templateId) payload.template_id = templateId;
      const { data } = await api.post("/whatsapp/send", payload);

      // Surface the actual reason the send skipped so the operator knows
      // whether it's a missing phone, a STOP-ed lead, or a real Twilio
      // error (which the WhatsApp Log has full details on).
      if (data.queued > 0) {
        toast.success(
          `${data.queued} WhatsApp message${data.queued === 1 ? "" : "s"} sent`
          + (data.skipped_count ? ` · ${data.skipped_count} skipped` : ""),
        );
      }
      if (data.skipped_count) {
        // Show up to 3 real reasons — dedupe short ones like "no phone".
        const seen = new Set();
        const lines = [];
        for (const s of (data.skipped || [])) {
          const r = (s.reason || "unknown").slice(0, 180);
          const key = `${s.business_name || s.lead_id}::${r}`;
          if (seen.has(key)) continue;
          seen.add(key);
          lines.push(`${s.business_name || "Lead"}: ${r}`);
          if (lines.length >= 3) break;
        }
        const more = data.skipped.length > lines.length ? ` · +${data.skipped.length - lines.length} more` : "";
        toast.error(
          <div className="space-y-1">
            <div className="font-semibold text-sm">{data.skipped_count} lead{data.skipped_count === 1 ? "" : "s"} skipped{more}</div>
            {lines.map((l, i) => <div key={i} className="text-xs">{l}</div>)}
            <div className="text-[10px] opacity-70 mt-1">See full detail in the WhatsApp Log.</div>
          </div>,
          { duration: 8000 },
        );
      }
      setSelection({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp send failed");
    } finally { setWaSending(false); }
  };
  const templatesByPipeline = useMemo(() => {
    const g = {};
    waTemplates.forEach((t) => {
      (g[t.pipeline] = g[t.pipeline] || []).push(t);
    });
    return g;
  }, [waTemplates]);
  const promoteToPipeline = async (targetKey) => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/growth/leads/copy-to-pipeline", {
        lead_ids: selectedIds, to_pipeline: targetKey,
      });
      const parts = [`+${data.created} cloned to ${targetKey.replace("b2c_", "")}`];
      if (data.skipped_dupes) parts.push(`${data.skipped_dupes} already there`);
      toast.success(parts.join(" · "));
      setSelection({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    }
  };
  const toggle = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const on = items.length > 0 && items.every((l) => selection[l.id]);
    if (on) setSelection({});
    else setSelection(Object.fromEntries(items.map((l) => [l.id, true])));
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/leads", {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          temperature: tempFilter || undefined,
          sort: sort || undefined,
          skip: page * PER, limit: PER,
        },
      });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error("Could not load leads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, tempFilter, sort, page]);
  useEffect(() => { const t = setTimeout(() => { setPage(0); load(); }, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { api.get("/growth/meta").then(({ data }) => setMeta(data)); }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/growth/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} leads (${data.skipped} skipped).`);
      setPage(0); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    }
  };

  const doExport = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Open a URL that includes the auth via header — we'll use fetch and blob
    fetch(`${API_URL}/growth/leads/export/csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "growth-leads.csv";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed."));
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    try {
      await api.delete(`/growth/leads/${id}`);
      setItems((it) => it.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error("Delete failed."); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="growth-leads">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Growth Engine · Leads</div>
          <h1 className="font-display text-3xl mt-1">Your prospect pipeline</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="leads-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full gap-2" data-testid="leads-import"><Upload className="h-4 w-4" /> Import CSV</Button>
          <Button variant="outline" onClick={doExport} className="rounded-full gap-2" data-testid="leads-export"><Download className="h-4 w-4" /> Export</Button>
          <Button onClick={() => setShowNew(true)} className="rounded-full gap-2" data-testid="leads-add"><Plus className="h-4 w-4" /> Add lead</Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="leads-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-3 rounded-full border border-border bg-background text-sm"
            placeholder="Search business, city, industry, contact…"
          />
        </div>
        <select data-testid="leads-filter-status" value={statusFilter} onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }} className="h-10 rounded-full border border-border bg-background px-4 text-sm capitalize">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="leads-filter-temp" value={tempFilter} onChange={(e) => { setPage(0); setTempFilter(e.target.value); }} className="h-10 rounded-full border border-border bg-background px-4 text-sm capitalize">
          <option value="">All temperatures</option>
          {["hot", "warm", "cold"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="leads-filter-sort" value={sort} onChange={(e) => { setPage(0); setSort(e.target.value); }} className="h-10 rounded-full border border-border bg-background px-4 text-sm">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="recent_touch">Recently touched</option>
          <option value="business">Business (A-Z)</option>
          <option value="score">Lead score</option>
        </select>
      </div>

      {/* Bulk-promote bar — appears when leads are selected. Copies (not moves)
          the selected leads into the target B2C pipeline so the operator keeps
          the original B2B record intact for continued sales work. */}
      {selectedIds.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="growth-selection-bar">
          <div className="text-sm font-medium">{selectedIds.length} selected</div>

          {/* Preflight chip — surfaces phone problems BEFORE the API fires */}
          {preflight && (preflight.skipped_count > 0 || preflight.ready_count > 0) && (
            <div
              className={
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs " +
                (preflight.skipped_count === 0
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"
                  : preflight.ready_count === 0
                  ? "border-destructive/40 bg-destructive/10 text-destructive"
                  : "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400")
              }
              data-testid="growth-preflight-chip"
              title={
                preflight.skipped
                  .slice(0, 5)
                  .map((s) => `${s.business_name}: ${s.reason}`)
                  .join("\n") || "All ready"
              }
            >
              <MessageCircle className="h-3 w-3" />
              <span>
                <b data-testid="growth-preflight-ready">{preflight.ready_count}</b>
                <span className="opacity-70"> of </span>
                <b>{preflight.total}</b>
                <span className="opacity-70"> ready</span>
                {preflight.skipped_count > 0 && (
                  <>
                    <span className="opacity-70"> · </span>
                    <b data-testid="growth-preflight-skipped">{preflight.skipped_count}</b>
                    <span className="opacity-70"> will be skipped</span>
                  </>
                )}
              </span>
            </div>
          )}
          {preflightLoading && !preflight && (
            <div className="text-xs text-muted-foreground">checking phones…</div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={waSending || (preflight && preflight.ready_count === 0)}
                className="rounded-full gap-1.5 ml-auto"
                data-testid="growth-whatsapp-bulk-btn"
                title={preflight && preflight.ready_count === 0 ? "No leads with valid phones" : ""}
              >
                <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                {waSending ? "Sending…" : "Send WhatsApp"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[480px] overflow-y-auto">
              {preflight && (
                <div className="px-2 py-2 border-b border-border mb-1" data-testid="growth-preflight-summary">
                  <div className="text-xs font-medium">
                    Sending to <b>{preflight.ready_count}</b> of {preflight.total} lead{preflight.total === 1 ? "" : "s"}
                  </div>
                  {preflight.skipped_count > 0 && (
                    <div className="mt-1.5 space-y-0.5 max-h-32 overflow-y-auto">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1">
                        {preflight.skipped_count} will be skipped
                      </div>
                      {preflight.skipped.slice(0, 4).map((s) => (
                        <div key={s.lead_id} className="text-[10px] leading-tight text-muted-foreground">
                          <span className="text-foreground/80">{s.business_name}</span>: {s.reason}
                        </div>
                      ))}
                      {preflight.skipped.length > 4 && (
                        <div className="text-[10px] italic text-muted-foreground">
                          + {preflight.skipped.length - 4} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <DropdownMenuLabel>Send to {preflight?.ready_count ?? selectedIds.length} lead{(preflight?.ready_count ?? selectedIds.length) === 1 ? "" : "s"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => sendWhatsApp()}
                data-testid="growth-wa-interactive"
                disabled={preflight && preflight.ready_count === 0}
              >
                <MessageCircle className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium">Interactive CTA (default)</span>
                  <span className="text-[10px] text-muted-foreground">3 tracked buttons: JAY 7 / Coffee / Training</span>
                </div>
              </DropdownMenuItem>
              {Object.entries(templatesByPipeline).length > 0 && <DropdownMenuSeparator />}
              {Object.entries(templatesByPipeline).map(([pkey, list]) => (
                <div key={pkey}>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-2">
                    {pkey.replace("b2c_", "").replace("b2b_", "")}
                  </DropdownMenuLabel>
                  {list.map((t) => (
                    <DropdownMenuItem
                      key={t.id}
                      onClick={() => sendWhatsApp(null, t.id)}
                      data-testid={`growth-wa-tpl-${t.id}`}
                      disabled={preflight && preflight.ready_count === 0}
                    >
                      <span className="truncate text-xs">{t.name}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="growth-promote-btn">
                <ArrowLeftRight className="h-3.5 w-3.5" /> Move to pipeline
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Clone {selectedIds.length} lead{selectedIds.length === 1 ? "" : "s"} to…</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PROMOTE_TARGETS.map((t) => (
                <DropdownMenuItem key={t.key} onClick={() => promoteToPipeline(t.key)} data-testid={`growth-promote-to-${t.key}`}>
                  <t.icon className={"h-4 w-4 mr-2 " + t.accent} /> {t.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
                Leaves the source lead intact in Growth Engine.
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelection({})} data-testid="growth-clear-selection">
            Clear
          </Button>
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground items-center">
          <div className="col-span-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={items.length > 0 && items.every((l) => selection[l.id])}
              onChange={toggleAll}
              className="cursor-pointer"
              data-testid="growth-select-all"
              aria-label="Select all"
            />
            Business
          </div>
          <div className="col-span-1">Type</div>
          <div className="col-span-2">Industry / City</div>
          <div className="col-span-1">Decision Maker</div>
          <div className="col-span-1">Score</div>
          <div className="col-span-1">Temp</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Added</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No leads yet.{" "}
            <button className="underline" onClick={() => setShowNew(true)}>Add your first one</button>{" "}
            or import a CSV.
          </div>
        ) : (
          items.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm hover:bg-secondary/40 transition">
              <div className="col-span-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!selection[l.id]}
                  onChange={() => toggle(l.id)}
                  className="cursor-pointer"
                  data-testid={`growth-select-${l.id}`}
                  aria-label={`Select ${l.business_name}`}
                />
                <div className="min-w-0">
                  <Link to={`/app/growth/leads/${l.id}`} className="font-medium hover:underline block truncate" data-testid={`lead-open-${l.id}`}>{l.business_name}</Link>
                  {l.website && <div className="text-[11px] text-muted-foreground truncate">{l.website}</div>}
                </div>
              </div>
              <div className="col-span-1">
                <AudienceBadge pipeline={l.pipeline} />
              </div>
              <div className="col-span-2 text-muted-foreground">
                <div>{l.industry || "—"}</div>
                <div className="text-[11px]">{[l.city, l.country].filter(Boolean).join(", ") || "—"}</div>
              </div>
              <div className="col-span-1 text-muted-foreground">
                <div className="truncate text-sm">
                  {l.decision_maker_name || <span className="text-[11px]">—</span>}
                </div>
                {l.decision_maker_role && <div className="text-[10px] truncate">{l.decision_maker_role}</div>}
              </div>
              <div className="col-span-1 tabular-nums">{l.lead_score ?? <span className="text-muted-foreground">—</span>}</div>
              <div className="col-span-1"><TempBadge t={l.temperature} /></div>
              <div className="col-span-1">
                <span className={"text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full " + (STATUS_COLORS[l.status] || "bg-secondary")}>{l.status}</span>
              </div>
              <div className="col-span-1 text-[11px] text-muted-foreground tabular-nums" title={l.created_at || ""}>
                {l.created_at ? new Date(l.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—"}
                {l.last_touched_at && l.last_touched_at !== l.created_at && (
                  <div className="text-[10px] opacity-70" title={`Last touched · ${l.last_touched_at}`}>
                    · {new Date(l.last_touched_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                  </div>
                )}
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                {(() => {
                  const p = (l.phone || "").trim();
                  const hasCC = p.startsWith("+");
                  const canSend = hasCC && !l.do_not_contact;
                  const hint = l.do_not_contact
                    ? "Lead opted out (STOP)"
                    : !p
                    ? "No phone on file"
                    : !hasCC
                    ? "Phone missing country code (needs + prefix)"
                    : "Send WhatsApp with CTA buttons";
                  return (
                    <button
                      onClick={() => sendWhatsApp([l.id])}
                      disabled={waSending || !canSend}
                      className="h-8 w-8 grid place-items-center rounded hover:bg-emerald-500/10 text-emerald-600 disabled:opacity-30 disabled:hover:bg-transparent"
                      title={hint}
                      data-testid={`lead-whatsapp-${l.id}`}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </button>
                  );
                })()}
                <Link to={`/app/growth/leads/${l.id}`}>
                  <button className="h-8 w-8 grid place-items-center rounded hover:bg-foreground/10" aria-label="Open" data-testid={`lead-open-btn-${l.id}`}>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
                <button onClick={() => remove(l.id)} className="h-8 w-8 grid place-items-center rounded hover:bg-destructive/20 text-destructive" aria-label="Delete" data-testid={`lead-delete-${l.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {total > PER && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>Showing {page * PER + 1}–{Math.min((page + 1) * PER, total)} of {total}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={(page + 1) * PER >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <NewLeadDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setPage(0); load(); }} meta={meta} />
    </div>
  );
}
